import { create } from 'zustand';
import { Email, Mailbox } from '@/types/jmap.types';

interface EmailState {
  mailboxes: Mailbox[];
  emails: Email[];
  emailListCache: Record<string, { emails: Email[]; fetchedAt: number }>;
  emailDetailCache: Record<string, { email: Email; fetchedAt: number }>;
  favoriteMailboxIds: string[];
  selectedMailbox: string | null;
  selectedEmail: string | null;
  drawerOpen: boolean;
  newMailCount: number;
  newCalendarCount: number;
  mailListRefreshToken: number;
  accountEmail: string | null;
  accountDisplayName: string | null;
  accountAvatarUrl: string | null;
  userAvatarCache: Record<string, { url: string | null; fetchedAt: number }>;
  setMailboxes: (mailboxes: Mailbox[]) => void;
  setEmails: (emails: Email[]) => void;
  setEmailListCache: (key: string, emails: Email[], fetchedAt?: number) => void;
  setEmailDetailCache: (email: Email, fetchedAt?: number) => void;
  clearEmailListCache: (key?: string) => void;
  clearEmailDetailCache: (emailId?: string) => void;
  setFavoriteMailboxIds: (ids: string[]) => void;
  toggleFavoriteMailbox: (mailboxId: string) => void;
  selectMailbox: (mailboxId: string) => void;
  selectEmail: (emailId: string | null) => void;
  removeEmail: (emailId: string) => void;
  updateEmail: (emailId: string, updates: Partial<Email>) => void;
  toggleDrawer: () => void;
  setDrawerOpen: (open: boolean) => void;
  incrementNewMailCount: (count?: number) => void;
  resetNewMailCount: () => void;
  incrementNewCalendarCount: (count?: number) => void;
  resetNewCalendarCount: () => void;
  bumpMailListRefresh: () => void;
  setAccountProfile: (email: string | null, displayName?: string | null, avatarUrl?: string | null) => void;
  setAccountAvatarUrl: (avatarUrl: string | null) => void;
  setUserAvatarCache: (email: string, url: string | null, fetchedAt?: number) => void;
  clearUserAvatarCache: (email?: string) => void;
}

const loadFavoriteMailboxIds = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('webmail_favorite_mailboxes');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const persistFavoriteMailboxIds = (ids: string[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('webmail_favorite_mailboxes', JSON.stringify(ids));
  } catch {}
};

export const useEmailStore = create<EmailState>((set) => ({
  mailboxes: [],
  emails: [],
  emailListCache: {},
  emailDetailCache: {},
  favoriteMailboxIds: loadFavoriteMailboxIds(),
  selectedMailbox: null,
  selectedEmail: null,
  drawerOpen: false,
  newMailCount: 0,
  newCalendarCount: 0,
  mailListRefreshToken: 0,
  accountEmail: null,
  accountDisplayName: null,
  accountAvatarUrl: null,
  userAvatarCache: {},
  setMailboxes: (mailboxes) => set({ mailboxes }),
  setEmails: (emails) =>
    set({
      emails: [...emails].sort((a, b) => {
        const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return db - da; // newest first
      }),
    }),
  setEmailListCache: (key, emails, fetchedAt = Date.now()) =>
    set((state) => ({
      emailListCache: {
        ...state.emailListCache,
        [key]: { emails, fetchedAt },
      },
    })),
  setEmailDetailCache: (email, fetchedAt = Date.now()) =>
    set((state) => ({
      emailDetailCache: {
        ...state.emailDetailCache,
        [email.id]: { email, fetchedAt },
      },
    })),
  clearEmailListCache: (key) =>
    set((state) => {
      if (!key) return { emailListCache: {} };
      const next = { ...state.emailListCache };
      delete next[key];
      return { emailListCache: next };
    }),
  clearEmailDetailCache: (emailId) =>
    set((state) => {
      if (!emailId) return { emailDetailCache: {} };
      const next = { ...state.emailDetailCache };
      delete next[emailId];
      return { emailDetailCache: next };
    }),
  setFavoriteMailboxIds: (ids) => {
    persistFavoriteMailboxIds(ids);
    set({ favoriteMailboxIds: ids });
  },
  toggleFavoriteMailbox: (mailboxId) =>
    set((state) => {
      const exists = state.favoriteMailboxIds.includes(mailboxId);
      const next = exists
        ? state.favoriteMailboxIds.filter((id) => id !== mailboxId)
        : [...state.favoriteMailboxIds, mailboxId];
      persistFavoriteMailboxIds(next);
      return { favoriteMailboxIds: next };
    }),
  selectMailbox: (mailboxId) =>
    set((state) => {
      if (state.selectedMailbox === mailboxId) return state;
      return { selectedMailbox: mailboxId, emails: [] };
    }),
  selectEmail: (emailId) => set({ selectedEmail: emailId }),
  removeEmail: (emailId) =>
    set((state) => {
      const nextCacheEntries = Object.entries(state.emailListCache).map(([key, entry]) => {
        return [key, { ...entry, emails: entry.emails.filter((email) => email.id !== emailId) }];
      });
      return {
        emails: state.emails.filter((email) => email.id !== emailId),
        selectedEmail: state.selectedEmail === emailId ? null : state.selectedEmail,
        emailDetailCache: Object.keys(state.emailDetailCache).length
          ? Object.fromEntries(Object.entries(state.emailDetailCache).filter(([key]) => key !== emailId))
          : state.emailDetailCache,
        emailListCache: Object.fromEntries(nextCacheEntries),
      };
    }),
  updateEmail: (emailId, updates) =>
    set((state) => {
      const applyUpdates = (email: Email) => {
        if (email.id !== emailId) return email;
        // Merge keywords map carefully to avoid losing other flags
        if (updates.keywords) {
          const merged = { ...(email.keywords || {}) } as any;
          Object.entries(updates.keywords as any).forEach(([k, v]) => {
            if (v === null) {
              delete merged[k as any];
            } else {
              merged[k as any] = v as any;
            }
          });
          return { ...email, ...updates, keywords: merged } as any;
        }
        return { ...email, ...updates } as any;
      };
      const nextCacheEntries = Object.entries(state.emailListCache).map(([key, entry]) => {
        return [key, { ...entry, emails: entry.emails.map(applyUpdates) }];
      });
      const existingDetail = state.emailDetailCache[emailId];
      const nextDetailCache = existingDetail
        ? { ...state.emailDetailCache, [emailId]: { ...existingDetail, email: applyUpdates(existingDetail.email) } }
        : state.emailDetailCache;
      return {
        emails: state.emails.map(applyUpdates),
        emailListCache: Object.fromEntries(nextCacheEntries),
        emailDetailCache: nextDetailCache,
      };
    }),
  toggleDrawer: () => set((state) => ({ drawerOpen: !state.drawerOpen })),
  setDrawerOpen: (open) => set({ drawerOpen: open }),
  incrementNewMailCount: (count = 1) =>
    set((state) => ({ newMailCount: state.newMailCount + Math.max(count, 0) })),
  resetNewMailCount: () => set({ newMailCount: 0 }),
  incrementNewCalendarCount: (count = 1) =>
    set((state) => ({ newCalendarCount: state.newCalendarCount + Math.max(count, 0) })),
  resetNewCalendarCount: () => set({ newCalendarCount: 0 }),
  bumpMailListRefresh: () => set((state) => ({ mailListRefreshToken: state.mailListRefreshToken + 1 })),
  setAccountProfile: (email, displayName = null, avatarUrl = null) =>
    set((state) => ({
      accountEmail: email,
      accountDisplayName: displayName,
      accountAvatarUrl: avatarUrl,
      userAvatarCache: state.accountEmail && state.accountEmail !== email ? {} : state.userAvatarCache,
    })),
  setAccountAvatarUrl: (avatarUrl) => set({ accountAvatarUrl: avatarUrl }),
  setUserAvatarCache: (email, url, fetchedAt = Date.now()) =>
    set((state) => ({
      userAvatarCache: {
        ...state.userAvatarCache,
        [email]: { url, fetchedAt },
      },
    })),
  clearUserAvatarCache: (email) =>
    set((state) => {
      if (!email) return { userAvatarCache: {} };
      const next = { ...state.userAvatarCache };
      delete next[email];
      return { userAvatarCache: next };
    }),
}));
